// Home = "My Day" (CLAIMS-WORKSPACE-FINISH-1, 2026-06-02).
// Answers "what should I work on next?" — a single prioritized work queue built
// from the EXISTING attention system (tasks + bills + the workbench attention
// counts). Honest empty state: only "all caught up" when there is genuinely
// nothing. Notifications are digested (grouped) instead of one row per item.
import { useState, useEffect, useCallback, useMemo } from "react";
import { apiFetch, fmt } from "../api";
import QuickAccess from "../components/nav/QuickAccess";
import { SummaryRibbon, RibbonCell } from "../components/ui";

const ACT_ICON = {
  note: "📝", task_created: "✓", task_completed: "✅", payment: "💵", time: "🕑",
  reserve_change: "💰", document: "📄", default: "•",
};

// event_type → [singular, plural] for the notification digest + My-Day attention rows.
const NOTIF_LABEL = {
  CLAIM_INACTIVE: ["inactive claim", "inactive claims"],
  RESERVE_REVIEW: ["reserve review", "reserve reviews"],
  DOCUMENT_REVIEW: ["document to review", "documents to review"],
  PAYMENT_REVIEW: ["payment to review", "payments to review"],
  PAYMENT_CODING_REQUIRED: ["payment to code", "payments to code"],
  PAYMENT_CODING_INCOMPLETE: ["coding split to fix", "coding splits to fix"],
  PAYMENT_REIMBURSEMENT_REVIEW: ["reimbursement to review", "reimbursements to review"],
  OVERDUE_TASK: ["overdue task", "overdue tasks"],
  HIGH_PRIORITY_TASK: ["high-priority task", "high-priority tasks"],
  task_assigned: ["task assigned to you", "tasks assigned to you"],
  bill_routed: ["bill to review", "bills to review"],
};
const labelFor = (et, n) => {
  const l = NOTIF_LABEL[et];
  if (l) return n === 1 ? l[0] : l[1];
  return (et || "notification").replace(/_/g, " ").toLowerCase() + (n === 1 ? "" : "s");
};

export default function Dashboard({ user, onNavigateToClaim, openTime, openTask, workRefresh, setPage }) {
  const [tasks, setTasks]   = useState([]);
  const [queue, setQueue]   = useState(null);
  const [summary, setSumm]  = useState(null);
  const [notifs, setNotifs] = useState({ items: [], unread_count: 0 });
  const [activity, setAct]  = useState([]);
  const [bills, setBills]   = useState([]);
  const [expanded, setExpanded] = useState({});   // notification digest groups
  const [actFilter, setActFilter] = useState("all"); // Recent Activity kind filter (LD-DESIGN-012 pattern 5)

  const load = useCallback(() => {
    apiFetch("/tasks/my").then(d => setTasks(d || [])).catch(() => {});
    apiFetch("/workbench/queue").then(setQueue).catch(() => {});
    apiFetch("/time/summary").then(setSumm).catch(() => {});
    apiFetch("/me/notifications").then(d => setNotifs(d || { items: [] })).catch(() => {});
    apiFetch("/me/activity?limit=24").then(d => setAct(d || [])).catch(() => {});
    apiFetch("/me/bill-review").then(d => setBills(d || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, workRefresh]);

  const deleteTask = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    try { await apiFetch(`/tasks/${id}`, { method: "DELETE" }); load(); }
    catch (err) { alert(err.message || "Delete failed."); }
  };
  const goWorkbench = () => setPage && setPage("claims");

  const todayMidnight = new Date(new Date().toDateString());
  const overdueTasks = tasks.filter(t => t.due_date && new Date(t.due_date) < todayMidnight);
  const otherTasks   = tasks.filter(t => !overdueTasks.includes(t));

  // Claim-level attention NOT already represented by a task or a bill row.
  const attnRows = queue ? [
    { key: "reserve_reviews", n: queue.reserve_reviews, label: labelFor("RESERVE_REVIEW", queue.reserve_reviews), color: "#d97706" },
    { key: "new_documents",   n: queue.new_documents,   label: labelFor("DOCUMENT_REVIEW", queue.new_documents),  color: "#7c3aed" },
    { key: "stale_claims",    n: queue.stale_claims,    label: labelFor("CLAIM_INACTIVE", queue.stale_claims),    color: "#6b7280" },
  ].filter(r => r.n > 0) : [];

  const actionCount = overdueTasks.length + otherTasks.length + bills.length;
  const hasWork = actionCount > 0 || attnRows.length > 0;

  // Notification digest: group by event_type.
  const notifGroups = useMemo(() => {
    const map = new Map();
    for (const n of (notifs.items || [])) {
      const k = n.event_type || "other";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(n);
    }
    return [...map.entries()].map(([et, items]) => ({ et, items }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [notifs]);

  // Recent activity: collapse runs of the same (event_type+title+claim) into one.
  const activityClean = useMemo(() => {
    const out = [];
    for (const a of activity) {
      const last = out[out.length - 1];
      if (last && last.event_type === a.event_type && last.title === a.title && last.claim_id === a.claim_id) {
        last._dupes = (last._dupes || 1) + 1; continue;
      }
      out.push({ ...a });
    }
    return out;
  }, [activity]);

  // LD-DESIGN-012 pattern 5: count-bearing filter pills derived from the kinds
  // actually present in the loaded feed (event_type on each row — honesty rule).
  const actKinds = useMemo(() => {
    const m = new Map();
    for (const a of activityClean) {
      const k = a.event_type || "other";
      m.set(k, (m.get(k) || 0) + 1);
    }
    return [...m.entries()].sort((x, y) => y[1] - x[1]);
  }, [activityClean]);
  const activityShown = actFilter === "all"
    ? activityClean
    : activityClean.filter(a => (a.event_type || "other") === actFilter);

  const hrs = (h) => (fmt.hours ? fmt.hours(h) : `${Number(h || 0).toFixed(1)}h`);
  const rowBase = { padding: "9px 14px", borderBottom: "1px solid var(--border)", cursor: "pointer", minWidth: 0 };
  // HOME-REDESIGN-1: compact empty states (the old centered .empty-state reserved
  // huge blank areas on a mostly-empty Home — anti-command-center).
  const emptyCompact = { padding: "11px 14px", fontSize: 12.5, color: "var(--text3)" };

  const TaskRow = (t, overdue) => (
    <div key={t.id} onClick={() => t.claim_id ? (onNavigateToClaim && onNavigateToClaim(t.claim_id, { tab: "tasks", taskId: t.id })) : (openTask && openTask({ task: t }))}
         style={{ ...rowBase, display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 4, flexShrink: 0,
        background: overdue ? "var(--red)" : ["High", "Critical"].includes(t.priority) ? "var(--yellow,#d97706)" : "var(--green)" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--navy)" }}>{t.title}</div>
        <div style={{ fontSize: 11, color: overdue ? "var(--red)" : "var(--text3)", marginTop: 1 }}>
          {t.claim_number
            ? <span className="mono">{t.claim_number}</span>
            : <span style={{ fontStyle: "italic" }}>Personal</span>}
          {t.due_date && ` · Due ${fmt.date(t.due_date)}`}{overdue && " ⚠ OVERDUE"}
          {t.assigned_to_name && <span style={{ color: "var(--text3)" }}> · {t.assigned_to_name}</span>}
        </div>
      </div>
      {["High", "Critical"].includes(t.priority) && <span className="badge" style={{ background: "#fef3c7", color: "#92400e", fontSize: 10 }}>{t.priority}</span>}
      <button title="Edit task" onClick={(e) => { e.stopPropagation(); openTask && openTask({ task: t }); }} style={taskIconBtn}>✎</button>
      <button title="Delete task" onClick={(e) => deleteTask(e, t.id)} style={{ ...taskIconBtn, color: "#dc2626" }}>🗑</button>
    </div>
  );

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "var(--navy)" }}>Good {timeOfDay()}, {user.full_name?.split(" ")[0]}</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-blue" onClick={() => openTime && openTime({})}>+ Time</button>
          <button className="btn btn-outline" onClick={() => openTask && openTask({})}>+ Task</button>
        </div>
      </div>

      {/* LD-DESIGN-010 SummaryRibbon — my-day vitals from data this page already loads. */}
      <SummaryRibbon>{[
        <RibbonCell key="tasks" icon="🗓️" label="Tasks Today" value={actionCount} color={actionCount > 0 ? "var(--navy)" : "var(--green)"} />,
        <RibbonCell key="notifs" icon="🔔" label="Notifications" value={(notifs.items || []).length} color={notifs.unread_count > 0 ? "#dc2626" : undefined} />,
        <RibbonCell key="today" icon="🕑" label="Hours Today" value={hrs(summary?.today_hours)} />,
        <RibbonCell key="week" label="Hours This Week" value={hrs(summary?.week_hours)} />,
        <RibbonCell key="billable" label="Billable (Wk)" value={hrs(summary?.week_billable_hours)} color={Number(summary?.week_billable_hours) > 0 ? "var(--green)" : undefined} />,
      ]}</SummaryRibbon>

      <QuickAccess />

      {/* Option A (Kuhrt 2026-07-03): act-left, happenings-right. Left = My Day +
          Notifications stacked (capped + scroll per LD-DESIGN-013); right = Recent
          Activity as the tall column. Time Summary card retired — the ribbon owns
          those totals (backlog: per-day week chart needs a per-day endpoint). */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 420px", minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="card">
            <div className="card-header"><span>🗓️</span><h3>My Day</h3>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text3)" }}>{actionCount} to do</span></div>

            {!hasWork && <div style={emptyCompact}>You're all caught up — nothing needs you right now.</div>}

            <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {overdueTasks.map(t => TaskRow(t, true))}

            {bills.map(b => (
              <div key={`b${b.id}`} onClick={() => onNavigateToClaim && onNavigateToClaim(b.claim_id, { tab: "financials", billId: b.id })}
                   style={{ ...rowBase, display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 0, flexShrink: 0, background: "var(--navy)" }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Review bill · {b.payee || "(bill)"}</span>
                <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: "var(--navy)" }}>{b.amount != null ? "$" + Number(b.amount).toLocaleString("en-US", { maximumFractionDigits: 0 }) : ""}</span>
                <span className="mono" style={{ flexShrink: 0, maxWidth: "34%", fontSize: 10.5, color: "var(--blue)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.claim_number}</span>
              </div>
            ))}

            {otherTasks.map(t => TaskRow(t, false))}

            {attnRows.length > 0 && (
              <div style={{ borderTop: hasWork && (overdueTasks.length || bills.length || otherTasks.length) ? "1px solid var(--border)" : "none" }}>
                <div style={{ padding: "8px 14px 4px", fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--text3)" }}>Also needs attention</div>
                {attnRows.map(r => (
                  <button key={r.key} onClick={goWorkbench} title="Open in Workbench"
                          style={{ ...rowBase, width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid var(--border)", background: "none", display: "flex", gap: 10, alignItems: "center" }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: r.color, minWidth: 22 }}>{r.n}</span>
                    <span style={{ flex: 1, fontSize: 12.5, color: "var(--text1,#1a2438)" }}>{r.label}</span>
                    <span style={{ color: "var(--text3)", fontSize: 12 }}>→</span>
                  </button>
                ))}
              </div>
            )}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span>🔔</span><h3>Notifications</h3>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text3)" }}>{(notifs.items || []).length}</span>
              {notifs.unread_count > 0 && <span style={{ marginLeft: 6, background: "#dc2626", color: "#fff", borderRadius: 10, fontSize: 10, fontWeight: 700, padding: "1px 7px" }}>{notifs.unread_count}</span>}</div>
            {notifGroups.length === 0
              ? <div style={emptyCompact}>Nothing needs your attention.</div>
              : <div style={{ maxHeight: 320, overflowY: "auto" }}>{notifGroups.map(g => {
                  const isOpen = !!expanded[g.et];
                  if (g.items.length === 1) {
                    const n = g.items[0];
                    return (
                      <div key={g.et} onClick={() => n.claim_id && onNavigateToClaim && onNavigateToClaim(n.claim_id, n.ref_table === "bill_intake_items" ? { tab: "financials", billId: n.ref_id } : null)}
                           style={{ padding: "8px 14px", borderBottom: "1px solid var(--border)", cursor: n.claim_id ? "pointer" : "default", display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--text1,#1a2438)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.title}</span>
                        {n.claim_number && <span className="mono" style={{ flexShrink: 0, maxWidth: "42%", fontSize: 10.5, color: "var(--blue)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.claim_number}</span>}
                      </div>
                    );
                  }
                  return (
                    <div key={g.et}>
                      <button onClick={() => setExpanded(e => ({ ...e, [g.et]: !e[g.et] }))}
                              style={{ width: "100%", textAlign: "left", border: "none", background: "none", padding: "9px 14px", borderBottom: "1px solid var(--border)", cursor: "pointer", display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "var(--navy)", minWidth: 22 }}>{g.items.length}</span>
                        <span style={{ flex: 1, fontSize: 12.5, color: "var(--text1,#1a2438)" }}>{labelFor(g.et, g.items.length)}</span>
                        <span style={{ color: "var(--text3)", fontSize: 11 }}>{isOpen ? "▲" : "▼"}</span>
                      </button>
                      {isOpen && g.items.slice(0, 25).map((n, i) => (
                        <div key={n.id || i} onClick={() => n.claim_id && onNavigateToClaim && onNavigateToClaim(n.claim_id, n.ref_table === "bill_intake_items" ? { tab: "financials", billId: n.ref_id } : null)}
                             style={{ padding: "6px 14px 6px 36px", borderBottom: "1px solid var(--border)", cursor: n.claim_id ? "pointer" : "default", display: "flex", gap: 8, alignItems: "center", minWidth: 0, background: "var(--bg2,#fafbfc)" }}>
                          <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: "var(--text2,#555)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.title}</span>
                          {n.claim_number && <span className="mono" style={{ flexShrink: 0, maxWidth: "42%", fontSize: 10, color: "var(--blue)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.claim_number}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })}</div>}
          </div>
        </div>

        <div className="card" style={{ flex: "1.15 1 460px", minWidth: 0 }}>
            <div className="card-header"><span>📡</span><h3>Recent Activity</h3>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text3)" }}>{activityClean.length}</span></div>
            {activityClean.length === 0
              ? <div style={emptyCompact}>No recent activity on your claims.</div>
              : (<>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, padding: "8px 14px 4px" }}>
                  {[["all", activityClean.length], ...actKinds].map(([k, n]) => {
                    const on = actFilter === k;
                    return (
                      <button key={k} onClick={() => setActFilter(k)}
                        style={{ padding: "2px 9px", fontSize: 10.5, borderRadius: 11, cursor: "pointer", fontWeight: on ? 700 : 500,
                                 border: `1px solid ${on ? "var(--navy)" : "var(--border)"}`,
                                 background: on ? "var(--navy)" : "#fff", color: on ? "#fff" : "var(--text2)" }}>
                        {k === "all" ? "All" : actLabel(k)} <span style={{ opacity: 0.7 }}>({n})</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ maxHeight: 560, overflowY: "auto" }}>
                {activityShown.slice(0, 40).map((a, i) => (
                  <div key={i} onClick={() => a.claim_id && onNavigateToClaim && onNavigateToClaim(a.claim_id)}
                       style={{ padding: "7px 14px", borderBottom: "1px solid var(--border)", cursor: a.claim_id ? "pointer" : "default", display: "flex", gap: 9, alignItems: "baseline" }}>
                    <span style={{ fontSize: 13 }}>{ACT_ICON[a.event_type] || ACT_ICON.default}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: "var(--text1,#1a2438)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {a.title}{a._dupes ? <span style={{ color: "var(--text3)" }}> ×{a._dupes}</span> : ""}</div>
                      <div style={{ fontSize: 10.5, color: "var(--text3)" }}>
                        <span className="mono" style={{ color: "var(--blue)" }}>{a.claim_number}</span>
                        {a.actor_name && ` · ${a.actor_name}`}{a.occurred_at && ` · ${fmt.date(a.occurred_at)}`}
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </>)}
        </div>
      </div>
    </div>
  );
}

// "task_created" → "Task created" (pill label from the raw event kind).
const actLabel = (k) => k.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());

const taskIconBtn = {
  background: "transparent", border: "none", cursor: "pointer", fontSize: 13,
  color: "var(--text3)", padding: "2px 4px", lineHeight: 1, flexShrink: 0,
};

function timeOfDay() {
  const h = new Date().getHours();
  return h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
}
